// ---------------------------------------------------------------------------------------------
// HER SIGNUP PAGE — **HER ARTWORK IS THE PAGE, AND THE FIELDS SIT IN THE SPACE SHE LEFT.**
//
// Her order (conversion surface 2 of three): *"her `signup.png` / `signup-vertical.png` AS THE PAGE
// (mockup-matching law); in her RESERVED SPACES the LIVE name and email fields, CONTINUE, and
// CONTINUE WITH GOOGLE wired real; EVERY SIGNUP TRIGGER SITE-WIDE LANDS HERE; the FUNNEL PRE-FILL
// LAW SURVIVES; the LOGIN path lands here too, with the existing-account path reachable so a
// returning member is never stranded; both viewports."*
//
// >>> EVERY NUMBER BELOW WAS READ OFF HER PIXELS, NOT PLACED BY EYE (R25/R180). <<< A probe scanned
// both masters for the gold hairlines her composition draws -- the card's two sides, each button's
// top and bottom edge, and the "or" rule -- and these are those rows and columns as PERCENTAGES of
// her artwork's own box. Her desktop master is 1672x941 and her phone master 941x1672, and the
// measured edges are:
//
//   DESKTOP  card x 66.51%..95.87% | google y 37.94%..43.68% | "or" y 49.10% | continue y 76.09%..82.57%
//   PHONE    card x 12.54%..87.04% | google y 63.64%..69.38% | "or" y 72.73% | continue y 87.08%..92.46%
//
// **THE RESERVED SPACE IS THE GAP BETWEEN HER "or" RULE AND HER CONTINUE BUTTON**, and it is the
// only place in either composition where she painted nothing. That is where the live fields go.
//
// >>> WHY THE MAP IS PERCENTAGES AND THE ART IS AN `<img>` -- HOME-1's OWN RULING, INHERITED RATHER
// THAN RE-DECIDED. <<< A CSS background must be sized `cover` (which crops her edges off at some
// aspect ratios) or `contain` (which letterboxes), and **both LIE about where her elements are**. A
// plain `<img>` in a positioned wrapper occupies exactly the box these percentages are measured
// against, at every width, with no arithmetic -- so a control landing on nothing is impossible by
// construction rather than by care.
//
// **AND NOTHING IN FLOW MAY LIVE INSIDE THAT WRAPPER**, which the home page paid for the same day:
// its review carousel was a child of the artwork's box for one build, the box grew 698px -> 866px,
// and all fifteen phone zones slid down off her painted elements with two of her paid doors ending
// under the mobile tab bar. Everything here is absolutely positioned.
//
// It imports only laws that import nothing themselves, which is the entry requirement that lets
// `scripts/signup-page.mjs` LOAD and RUN every line of it with no browser (R146: reach is transitive).
import { emailFieldState } from "./emailAddress.js";
import { HOME_ART_BREAKPOINT } from "./homeTapMap.js";

/**
 * >>> ONE BREAKPOINT FOR THE WHOLE HOUSE'S ARTWORK, READ FROM WHERE IT ALREADY LIVES (R28/R29). <<<
 *
 * Her two compositions are DIFFERENT LAYOUTS rather than one scaled, exactly as her home art is, so
 * the map has to be chosen by the same width the `<picture>` chooses the FILE by. A second number
 * here would be a second answer to *which of her designs is on the screen* -- and the failure is
 * silent: the phone map over the desktop painting is a whole page of dead controls at one range of
 * widths, with nothing red anywhere.
 */
export const SIGNUP_ART_BREAKPOINT = HOME_ART_BREAKPOINT;

/**
 * >>> HER PAINTED CONTROLS, AS TRANSPARENT TARGETS OVER HER OWN PIXELS. <<<
 *
 * Nothing here is rebuilt as type: "Continue with Google" and "Continue" are drawn INTO her
 * composition, so what this adds is a target over each one. A rebuilt button is a second, drifting
 * copy of a thing she has already drawn (R28/R29) and it stops being her page the first time a font
 * falls back.
 *
 * `kind` is what the target DOES, and it is the whole roster: an unrostered kind reaches no door.
 */
export const DESKTOP_CONTROLS = Object.freeze([
  Object.freeze({ id: "google", kind: "google", label: "Continue with Google", left: 70.0, top: 37.94, width: 22.45, height: 5.74 }),
  Object.freeze({ id: "continue", kind: "continue", label: "Continue", left: 70.0, top: 76.09, width: 22.45, height: 6.48 }),
]);

export const PHONE_CONTROLS = Object.freeze([
  Object.freeze({ id: "google", kind: "google", label: "Continue with Google", left: 19.9, top: 63.64, width: 60.2, height: 5.74 }),
  Object.freeze({ id: "continue", kind: "continue", label: "Continue", left: 19.9, top: 87.08, width: 60.2, height: 5.38 }),
]);

/**
 * >>> HER RESERVED SPACE: THE GAP BETWEEN THE "or" RULE AND CONTINUE. <<<
 *
 * The band is given a little air at each end rather than butting against her painted rule and her
 * painted button, because a live field whose border touches one of her hairlines reads as a
 * rendering fault rather than as her design.
 *
 * **THE PHONE BAND IS THE TIGHT ONE AND ITS ARITHMETIC IS WRITTEN DOWN.** 13.0% of a 941x1672
 * master is 217px; on a 393px-wide phone her artwork is 393x698, so the band is **91px** and two
 * fields at HI1's 44px floor need 88px. It fits, with no room for a gap -- which is why the two
 * fields SHARE an edge and are separated by a hairline rather than by empty space. On any phone wider
 * than 393 it is roomier, and the rendered walk measures it rather than trusting this note.
 */
export const DESKTOP_FIELD_BAND = Object.freeze({ left: 70.0, top: 50.0, width: 22.45, height: 25.3 });
export const PHONE_FIELD_BAND = Object.freeze({ left: 19.9, top: 73.6, width: 60.2, height: 13.0 });

/** The map the browser's chosen artwork is measured against. */
export function signupControls(wide) {
  return wide ? DESKTOP_CONTROLS : PHONE_CONTROLS;
}
export function signupFieldBand(wide) {
  return wide ? DESKTOP_FIELD_BAND : PHONE_FIELD_BAND;
}

/**
 * A target is positioned from its CENTRE, so the sheet's `min-width`/`min-height` can grow one that
 * is smaller than HI1's 44px floor without sliding her painted element into a corner of it. Her
 * Google button is 5.74% of a 670px-tall artwork on a 1440px desktop -- **38px, under the floor** --
 * so this is load-bearing rather than tidy. HOME-1's own `zoneCenter`, one surface over.
 */
export function controlCenter(zone = {}) {
  const { left, top, width, height } = zone;
  if ([left, top, width, height].some((n) => typeof n !== "number" || !Number.isFinite(n))) return null;
  return { x: left + width / 2, y: top + height / 2 };
}

/**
 * ================================================================================================
 * >>> WHAT THE CONTINUE BUTTON DOES WITH WHAT IS IN THE TWO BOXES. <<<
 *
 * THREE OUTCOMES, NEVER TWO (R18), and the first two are why: *she has not finished typing* and
 * *that address cannot receive mail* are different facts with different sentences, and collapsing
 * them tells a member who has typed nothing that her address is wrong -- **the fastest way to lose a
 * signup on the one page her advertising pays to bring her to** (C8b's own ruling on this exact
 * field, one door over).
 *
 * >>> **IT ASKS ABOUT THE ADDRESS AND NOTHING ELSE, AND THE RULING FOR THAT IS WRITTEN BELOW
 * `signupMaySubmit` WITH THE MEMBER'S OWN WORDS ON IT.** <<< A fourth outcome used to lead this
 * list -- *"Add your name to continue."* -- and on the day this page became the sign-in door it
 * started refusing members who were signing in.
 *
 * **THE ADDRESS LAW IS `emailFieldState`, IMPORTED RATHER THAN WRITTEN AGAIN (R28/R29).** That is
 * the law the signup form and the mail rail already share, and this house has fourteen live accounts
 * that cannot receive mail because a second, looser answer to *is this an address* was in force. A
 * third spelling here would be a third audience.
 * ================================================================================================
 */
export function signupSubmitState({ email = "" } = {}) {
  const typedEmail = String(email ?? "").trim();
  if (!typedEmail) return "needs-email";
  // Her own suggester's verdict: only a BLOCKING state refuses. A "did you mean gmail.com?" is an
  // offer rather than a wall, because `josie@0825.com` is a real address (C8b/her signup-address
  // ruling) -- and refusing wrongly here costs her the member outright.
  return emailFieldState(typedEmail).blocking ? "invalid-email" : "ready";
}

/** Only one of the three states may put a request on the wire. */
export function signupMaySubmit(state) {
  return state === "ready";
}

/**
 * ================================================================================================
 * >>> **THE ADDRESS IDENTIFIES HER; THE NAME BELONGS TO A BIRTH.** (2026-09-04, out of a member's
 * own words at the desk.) <<<
 *
 * `rshillpinney@mindspring.com`, c199: *"when I go to website on my phone the site wont allow me to
 * sign in, it prompts me to sign up as if I'm new to site."* She was right, and this law was why.
 * AUTH-BREACH-1 made this page the ONE sign-in door on 2026-09-03 -- and the verdict above still
 * asked for a NAME first. **A member coming back has no name to type**: hers is already on file, and
 * `/api/auth/login` refuses a known address on EXISTENCE alone, without ever reading the name. So
 * Continue answered *"Add your name to continue."* to a woman who had typed the right address, and
 * the password box she needed was on the far side of that sentence. She spent three claim links in
 * twenty-three minutes and then wrote to the desk.
 *
 * >>> **THE ASK IS KEYED ON EXISTENCE, EXACTLY AS THE REFUSAL ONE DOOR OVER IS.** <<< Only the
 * server knows whether an address is a BIRTH or a RETURN, so only the server may ask for the name --
 * and it asks BELOW the existence refusal, so a returning member is never asked at all. The page
 * shows what comes back rather than guessing, and so it can no longer guess wrong.
 *
 * **THE SENTENCE IS STILL `SIGNUP_HINTS["needs-name"]`, STILL SPELLED ONCE (R28/R29).** What moved
 * is WHO decides, never what she reads.
 * ================================================================================================
 */
export const NAME_REQUIRED_CODE = "name-required";

/** A birth needs a name. A return never does. Whitespace is not a name. */
export function birthNeedsName(name = "") {
  return !String(name ?? "").trim();
}

/**
 * >>> THE SENTENCE SHE READS, AND IT NEVER NAMES THE MACHINERY (S1-A/R117). <<< A member does not
 * have a "field state"; she has a box she has not filled in yet. An unrostered state answers "" so
 * a future outcome cannot fall through to a plausible wrong sentence (R25's own shape one roster
 * over) -- silence is recoverable, a wrong instruction is not.
 */
export const SIGNUP_HINTS = Object.freeze({
  "needs-name": "Add your name to continue.",
  "needs-email": "Add your email to continue.",
  "invalid-email": "That email does not look like one we can reach you at.",
  ready: "",
});
export function signupHint(state) {
  return Object.prototype.hasOwnProperty.call(SIGNUP_HINTS, state) ? SIGNUP_HINTS[state] : "";
}

/**
 * ================================================================================================
 * >>> HER "THE LOGIN PATH LANDS HERE TOO, SO A RETURNING MEMBER IS NEVER STRANDED". <<<
 *
 * This page is the ONE door now, and the two people who arrive at it are not the same person: one
 * has never been here, and one has an account and expects to be let back in. **Her artwork paints
 * ONE card and one Continue**, so the page may not grow a second form -- and it does not have to,
 * because `/api/auth/login` has always been both: it creates the member if the address is new and
 * resumes her if it is not.
 *
 * So the SENTENCE is what changes, never the door: a member arriving by the LOGIN trigger is told
 * this is where she signs back in, and the same Continue carries her. **`intent` defaults to
 * `join`**, so every existing caller is byte-identical (ITEM 12's neutrality argument) and the only
 * surface that changes is the one that asks for the other word.
 * ================================================================================================
 */
export const SIGNUP_INTENTS = Object.freeze(["join", "login"]);
export function signupIntentValid(intent) {
  return SIGNUP_INTENTS.includes(intent);
}
export const SIGNUP_INTENT_LINES = Object.freeze({
  join: "Create your room. Your name and email, and you are in.",
  login: "Welcome back. The same email you used before brings you straight to your room.",
});
export function signupIntentLine(intent) {
  return signupIntentValid(intent) ? SIGNUP_INTENT_LINES[intent] : SIGNUP_INTENT_LINES.join;
}
